/**
 * The password rule, in one place, because it is currently in three and they disagree.
 *
 * The Cognito pool requires at least 8 characters with a digit and a symbol (`auth-stack.ts`,
 * from `MA-F1-003` / ADR 015). The API's schemas checked length alone. So a twelve-character
 * password with no symbol passed every check we made, and Cognito refused it at
 * `AdminSetUserPassword` — after the invitation link had been consumed, from a route whose error
 * handler had no case for it, which is why an invited administrator saw "Something went wrong on
 * our side" and could not get in.
 *
 * The auth stack's own comment had already named this failure mode for the *case* requirements:
 * "the first place a user meets that contradiction is a failed sign-up". It was right, and the
 * digits-and-symbols half of the same policy went unenforced anyway.
 *
 * So: one definition, imported by the API's request schemas and rendered by the screens that ask
 * for a password. The pool is still the authority — this mirrors it, and `RULES` exists so a
 * screen can state the requirement rather than leaving somebody to guess at it.
 */
/** Mirrors `passwordPolicy` in `infrastructure/aws/cdk/lib/auth-stack.ts`. */
export const PASSWORD_POLICY = {
    minimumLength: 8,
    requiresDigit: true,
    requiresSymbol: true,
    /** Deliberately off in the pool: CDK's defaults enable both. */
    requiresUppercase: false,
    requiresLowercase: false,
};
/** What a screen shows before somebody types, so the rule is met rather than discovered. */
export const PASSWORD_RULES = [
    `At least ${String(PASSWORD_POLICY.minimumLength)} characters`,
    'At least one number',
    'At least one symbol',
];
/**
 * The rule as one line, for the hint under a password field. The member sign-up's own words
 * (`registration-step-form.tsx`), reused on the Recover access screens so the rule reads the same
 * wherever a password is chosen (TW 44131867).
 */
export const PASSWORD_HINT = `At least ${String(PASSWORD_POLICY.minimumLength)} characters, with a number and a symbol.`;
/**
 * What a Recover access screen says once a reset by emailed code has worked (`USR-004` AC-9,
 * TW 44245088, Sumit 2026-09-30). The code route signs the person in and lands them where they
 * were going, so without this nothing on screen says the reset happened. The emailed link route
 * ends on its own "Your password is set" and does not use it.
 */
export const PASSWORD_RESET_SIGNED_IN_NOTICE = "Your password is updated, and you're signed in.";
/** Anything Cognito counts as a symbol, per its published policy. */
const SYMBOLS = /[\^$*.[\]{}()?"!@#%&/\\,><':;|_~`+=\- ]/;
const DIGIT = /[0-9]/;
/**
 * Whitespace at either end, which Cognito's API refuses before its password policy runs (its
 * pattern is `^[\S]+.*[\S]+$`). A space **inside** a password is fine and counts as a symbol;
 * one at the start or end — usually a phone keyboard or a password manager's doing — passed this
 * rule and was refused at sign-up as a 500 (TW 44272643).
 */
export const EDGE_WHITESPACE = /^\s|\s$/u;
/** What a password field says when only the edge-whitespace rule is unmet. */
export const PASSWORD_EDGE_SPACE_MESSAGE = "Your password can't start or end with a space.";
/**
 * Why this password is unacceptable, or `null` if it is fine.
 *
 * One message naming every unmet rule, rather than one per failure: a person changing a password
 * should not have to discover the requirements one rejection at a time.
 */
export function describePasswordViolation(password) {
    const unmet = [];
    if (password.length < PASSWORD_POLICY.minimumLength) {
        unmet.push(`be at least ${String(PASSWORD_POLICY.minimumLength)} characters`);
    }
    if (PASSWORD_POLICY.requiresDigit && !DIGIT.test(password))
        unmet.push('contain a number');
    if (PASSWORD_POLICY.requiresSymbol && !SYMBOLS.test(password))
        unmet.push('contain a symbol');
    if (EDGE_WHITESPACE.test(password))
        unmet.push('not start or end with a space');
    if (unmet.length === 0)
        return null;
    return `Password must ${unmet.join(', and ')}.`;
}
/**
 * The fragment key the reset mail's link carries the address under — `…/recovery#email=<address>`
 * (TW 44131947, Sumit 2026-09-18). The Cognito CustomMessage trigger writes it and cannot import
 * this file, so `infrastructure/aws/cdk/__tests__/a-verification-mail-carries-a-link.test.ts` reads
 * this line as text and fails if the two disagree.
 *
 * **A fragment, not a query parameter.** A browser never sends the part after `#` to a server, so
 * the address stays out of CloudFront's access logs and out of any `Referer` — it is the person's
 * own address, but a log is not where it belongs.
 */
export const RECOVERY_ADDRESS_FRAGMENT_PARAM = 'email';
/** The address a Recover access link carries, or `undefined` for a URL fragment without one. */
export function recoveryAddressFromFragment(hash) {
    const value = new URLSearchParams(hash.replace(/^#/u, '')).get(RECOVERY_ADDRESS_FRAGMENT_PARAM);
    return value === null || value.trim().length === 0 ? undefined : value.trim();
}
//# sourceMappingURL=password-policy.js.map